import React, { useRef, useState, useEffect } from 'react';
import { BarChart3 } from 'lucide-react';
import { PositionProductionData } from '../../types';

interface PositionProductionChartProps {
  positionProduction: PositionProductionData;
  onWeekClick?: (week: number) => void;
}

/**
 * PositionProductionChart - Small multiples line charts for position production
 * Features:
 * - Individual charts for QB/RB/WR/TE/K/DST
 * - Weekly totals over time
 * - Hover to see week values
 * - Click week to jump to week view
 */
// Custom hook for responsive chart sizing
const useChartDimensions = (containerRef: React.RefObject<HTMLDivElement>) => {
  const [dimensions, setDimensions] = useState({ width: 300, height: 80 });
  
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const chartWidth = Math.max(containerWidth - 32, 200); // Account for padding, min 200px
        setDimensions({ width: chartWidth, height: 80 });
      }
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [containerRef]);
  
  return dimensions;
};

const PositionProductionChart: React.FC<PositionProductionChartProps> = ({
  positionProduction,
  onWeekClick
}) => {
  const positions = ['QB', 'RB', 'WR', 'TE', 'K', 'D/ST'];
  
  const getPositionColor = (position: string) => {
    const colors: { [key: string]: string } = {
      'QB': '#8b5cf6', // purple
      'RB': '#10b981', // green
      'WR': '#3b82f6', // blue
      'TE': '#f59e0b', // yellow
      'K': '#f97316', // orange
      'D/ST': '#ef4444', // red
    };
    return colors[position] || '#6b7280';
  };

  const MiniChart: React.FC<{
    position: string;
    data: { week: number; total_points: number }[];
  }> = ({ position, data }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { width, height } = useChartDimensions(containerRef);
    const padding = 16;
    
    if (!data || data.length === 0) {
      return (
        <div ref={containerRef} className="bg-slate-900/30 rounded-lg p-4 text-center w-full">
          <div className="text-slate-100 font-semibold mb-2">{position}</div>
          <div className="text-slate-400 text-sm">No data</div>
        </div>
      );
    }

    const maxPoints = Math.max(...data.map(d => d.total_points));
    const minPoints = Math.min(...data.map(d => d.total_points));
    const range = maxPoints - minPoints || 1;

    // Create path for line chart
    const pathData = data.map((point, index) => {
      const x = padding + (index / (data.length - 1)) * (width - 2 * padding);
      const y = height - padding - ((point.total_points - minPoints) / range) * (height - 2 * padding);
      return { x, y, ...point };
    });

    const pathString = pathData
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
      .join(' ');

    const totalPoints = data.reduce((sum, d) => sum + d.total_points, 0);
    const avgPoints = totalPoints / data.length;
    const color = getPositionColor(position);

    return (
      <div ref={containerRef} className="bg-slate-900/30 rounded-lg p-4 hover:bg-slate-900/50 transition-colors w-full">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-slate-100 font-semibold" style={{ color }}>
            {position}
          </h4>
          <div className="text-right">
            <div className="text-slate-100 text-sm font-bold">
              {totalPoints.toFixed(1)}
            </div>
            <div className="text-slate-400 text-xs">
              {avgPoints.toFixed(1)} avg
            </div>
          </div>
        </div>
        
        <div className="relative w-full">
          <svg 
            viewBox={`0 0 ${width} ${height}`}
            className="w-full h-20 overflow-visible"
            preserveAspectRatio="xMidYMid meet"
          >
            {/* Grid lines */}
            <defs>
              <pattern
                id={`grid-${position}`}
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 20 0 L 0 0 0 20"
                  fill="none"
                  stroke="rgb(51, 65, 85)"
                  strokeWidth="0.5"
                  opacity="0.3"
                />
              </pattern>
            </defs>
            <rect width={width} height={height} fill={`url(#grid-${position})`} />
            
            {/* Line */}
            <path
              d={pathString}
              stroke={color}
              strokeWidth={2}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            
            {/* Area fill */}
            <path
              d={`${pathString} L ${pathData[pathData.length - 1].x} ${height - padding} L ${pathData[0].x} ${height - padding} Z`}
              fill={color}
              fillOpacity={0.1}
            />
            
            {/* Data points */}
            {pathData.map((point, index) => (
              <circle
                key={index}
                cx={point.x}
                cy={point.y}
                r={3}
                fill={color}
                className="cursor-pointer hover:r-4 transition-all"
                onClick={() => onWeekClick?.(point.week)}
              >
                <title>
                  Week {point.week}: {point.total_points.toFixed(1)} starter pts
                </title>
              </circle>
            ))}
          </svg>
          
          {/* Week labels */}
          <div className="flex justify-between text-xs text-slate-400 mt-2">
            <span>W1</span>
            <span>W{data.length}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-slate-800 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-slate-100 flex items-center space-x-2">
          <BarChart3 className="w-5 h-5 text-blue-400" />
          <span>Position Production Over Time</span>
        </h3>
        <div className="text-slate-400 text-sm">
          Weekly starter points by position
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        {positions.map((position) => {
          const data = positionProduction[position] || [];
          return (
            <div key={position} className="min-w-0 flex-1">
              <MiniChart
                position={position}
                data={data}
              />
            </div>
          );
        })}
      </div>

      {/* Summary Stats */}
      <div className="mt-6 pt-4 border-t border-slate-700">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {positions.map((position) => {
            const data = positionProduction[position] || [];
            const totalPoints = data.reduce((sum, d) => sum + d.total_points, 0);
            const bestWeek = data.reduce((best, current) => 
              current.total_points > best.total_points ? current : best, 
              { week: 0, total_points: 0 }
            );
            
            return (
              <div key={position} className="text-center">
                <div 
                  className="text-lg font-bold"
                  style={{ color: getPositionColor(position) }}
                >
                  {totalPoints.toFixed(1)}
                </div>
                <div className="text-slate-400 text-sm">{position} Starter Total</div>
                {bestWeek.total_points > 0 && (
                  <div className="text-slate-500 text-xs">
                    Best: W{bestWeek.week} ({bestWeek.total_points.toFixed(1)})
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PositionProductionChart;
